import debounce from 'lodash/debounce';
import { component } from 'picoapp';
import Drawer from '../../utils/drawer';
import Image from '../../utils/image';
import { clear } from '../../utils/node';

export default component(async (node, ctx) => {
  /**
   * Data + elements
   */
  const outputMobile = node.querySelector('.js-outputMobile');
  const outputDesktopOdd = node.querySelector('.js-outputDesktopOdd');
  const outputDesktopEven = node.querySelector('.js-outputDesktopEven');

  const submit = node.querySelector('button[type="submit"]');
  const resultCount = node.querySelector('.js-resultCount');
  const countOfTotal = node.querySelector('.js-countOfTotal');
  const loadMoreBtn = node.querySelector('.js-loadMoreBtn');

  const filterDrawer = node.querySelector('.js-filterDrawer');
  const filterDrawerBtns = node.querySelectorAll('.js-filterDrawerBtn');

  const filterBarScroll = node.querySelector('.js-filterBarScroll');
  const filterBarStatic = node.querySelector('.js-filterBarStatic');

  const filterBarActiveClass = 'collections__filter-bar--active';
  const filterBarHeaderActiveClass = 'collections__filter-bar--header-active';

  const { handle: currentHandle, pageSize } = JSON.parse(node.querySelector('[data-config]').textContent);

  let data = { count: 0, articles: [] };
  let currentPage = 1;

  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const renderArticle = (index, article) => {
    const itemMobile = document.createElement('li');
    outputMobile.appendChild(itemMobile);

    const itemDesktop = document.createElement('li');
    const indexIsOdd = (index + 1) % 2 !== 0;

    if (indexIsOdd) {
      outputDesktopOdd.appendChild(itemDesktop);
    } else {
      outputDesktopEven.appendChild(itemDesktop);
    }

    const markup = window.theme.templates.articleItem
      .replaceAll('%blogTitle%', article.blogTitle)
      .replaceAll('%url%', article.url)
      .replaceAll('%title%', article.title)
      .replace('%image%', Image(article.image))
      .replaceAll('%date%', article.date)
      .replaceAll('%readingTime%', article.readingTime)
      .replace('%author%', article.author);

    itemMobile.outerHTML = markup;
    itemDesktop.outerHTML = markup;
  };

  const updateCounts = (paginated) => {
    const base = window.theme.strings.blog;

    if (resultCount) resultCount.textContent = base.viewResults.replace('%count%', data.count);
    countOfTotal.textContent = base.pagination.replace('%count%', paginated).replace('%total%', data.count);
  };

  const updateState = async () => {
    updateCounts(clamp(currentPage * pageSize, 0, data.count));

    if (data.count > pageSize) {
      loadMoreBtn.hidden = false;
    } else {
      loadMoreBtn.hidden = true;
    }
  };

  loadMoreBtn.addEventListener('click', () => {
    currentPage += 1;

    const render = data.articles.slice(
      pageSize * (currentPage - 1),
      clamp(pageSize * currentPage, 0, data.count),
    );

    render.forEach((article, index) => renderArticle(index, article));
    updateCounts(clamp(currentPage * pageSize, 0, data.count));

    if (pageSize * currentPage > data.count) {
      loadMoreBtn.hidden = true;
    }
  });

  const getArticles = async (categories) => {
    if (!categories.length) return;

    data = { count: 0, articles: [] };
    const promises = [];

    categories.forEach((category) => {
      const promise = fetch(`/blogs/${category}?view=data`).then((response) => response.json());
      promises.push(promise);
    });

    const results = await Promise.all(promises);

    results.forEach((result) => {
      data.count += result.count;
      data.articles = [...data.articles, ...result.articles];
    });

    data.articles.sort((a, b) => b.publishedAt - a.publishedAt);

    if (submit) submit.disabled = false;
  };

  const handleChange = async () => {
    const categories = new FormData(filterDrawer).getAll('category');
    const params = new URLSearchParams();
    categories.forEach((category) => params.append('category', category));

    const url = `/blogs/${currentHandle}${categories.length ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', url);

    await getArticles(categories);
    updateState();
  };

  const handleSubmit = async (event) => {
    const params = new URLSearchParams(window.location.search);
    const categories = params.getAll('category');

    event.preventDefault();
    currentPage = 1;

    if (!categories.length) {
      return;
    }

    clear(outputMobile);
    clear(outputDesktopOdd);
    clear(outputDesktopEven);

    const render = data.articles.slice(
      pageSize * (currentPage - 1),
      clamp(pageSize * currentPage, 0, data.count),
    );

    render.forEach((article, index) => renderArticle(index, article));
    updateState();
  };

  if (filterDrawer) {
    filterDrawer.addEventListener('change', () => {
      submit.disabled = true;
      debounce(() => handleChange(), 750)();
    });

    filterDrawer.addEventListener('submit', handleSubmit);
  }

  const initFilterDrawer = () => {
    const closeBtns = filterDrawer.querySelectorAll('.js-closeBtn');
    const drawer = Drawer(filterDrawer);

    filterDrawerBtns.forEach((btn) => {
      btn.addEventListener('click', () => drawer.open());
    });

    closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => drawer.close());
    });
  };

  let previousScrollPos = window.scrollY;
  const handleScroll = () => {
    let scrollingUp = false;
    const currentScrollPos = window.scrollY;

    if (currentScrollPos < previousScrollPos) {
      scrollingUp = true;
    }

    previousScrollPos = currentScrollPos;

    if (!filterBarStatic || !filterBarScroll) return;
    const scrolledPastFilterBarStatic = filterBarStatic.getBoundingClientRect().bottom < 0;

    if (scrolledPastFilterBarStatic) {
      filterBarScroll.classList.add(filterBarActiveClass);
      filterBarScroll.classList.add(filterBarHeaderActiveClass);
    } else {
      filterBarScroll.classList.remove(filterBarActiveClass);
      filterBarScroll.classList.remove(filterBarHeaderActiveClass);
    }

    if (scrollingUp && scrolledPastFilterBarStatic) {
      filterBarScroll.classList.add(filterBarHeaderActiveClass);
    }
  };

  const updateActiveItems = (categories) => {
    const allCategories = filterDrawer.querySelectorAll('[name="category"]');
    const allCategoryNames = [...allCategories].map((category) => category.value);
    const validCategories = allCategoryNames.filter((category) => categories.includes(category));

    allCategories.forEach((category) => {
      category.checked = false;
    });

    validCategories.forEach((category) => {
      const item = filterDrawer.querySelector(`#category-${category}`);
      item.checked = true;
    });
  };

  const initFilter = async () => {
    const params = new URLSearchParams(window.location.search);
    let categories = params.getAll('category');

    if (!categories.length) {
      const checkedCategories = new FormData(filterDrawer).getAll('category');

      if (checkedCategories.length) {
        categories = checkedCategories;
      } else {
        categories = [currentHandle];
      }
    }

    await getArticles(categories);
    updateActiveItems(categories);
    updateState();

    clear(outputMobile);
    clear(outputDesktopOdd);
    clear(outputDesktopEven);

    const render = data.articles.slice(
      pageSize * (currentPage - 1),
      clamp(pageSize * currentPage, 0, data.count),
    );

    render.forEach((article, index) => renderArticle(index, article));
  };

  ctx.on('window:scroll', handleScroll);
  handleScroll();

  if (filterDrawer) {
    initFilterDrawer();
  }

  initFilter();
});
